import React, { ReactNode } from 'react';
import { DashboardLayout } from './DashboardLayout';
import { OnboardingTopBar } from './OnboardingTopBar';
import { useOnboardingSession } from '../../hooks/useOnboardingSession';

interface SettingsChromeProps {
  children: ReactNode;
}

/**
 * Chrome wrapper for the Settings routes.
 *
 * Settings must stay reachable mid-onboarding (Profile → Delete Account is
 * linked from the onboarding top bar, and `OnboardingGuard` carves `/settings`
 * out of its forced redirect). But a user whose organization onboarding is not
 * finished should not see the full production sidebar (Home, Inbox, Develop,
 * Inspect, Govern…) — those destinations just bounce them back to onboarding.
 *
 * So while onboarding is incomplete we render Settings inside the same slim
 * top-bar-only shell as onboarding; once onboarding is done we fall back to the
 * normal dashboard shell with the sidebar. Keeping this decision here avoids
 * leaking onboarding-awareness into `DashboardLayout` for every route.
 */
export const SettingsChrome: React.FC<SettingsChromeProps> = ({ children }) => {
  const { session } = useOnboardingSession();
  const shouldOnboard = session?.shouldOnboard === true;

  if (!shouldOnboard) {
    return <DashboardLayout>{children}</DashboardLayout>;
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <OnboardingTopBar />
      <main className="mx-auto w-full max-w-screen-2xl min-w-0 flex-1 p-4 md:p-6 2xl:p-10">
        {children}
      </main>
    </div>
  );
};
